/**
 * ProductGalleryEditor
 * --------------------
 * Combined gallery editor for a product. Merges:
 *   • Printful-sourced images (`gallery_images`, read-only, refreshed by sync)
 *   • Editorial images (`user_gallery_images`, Planet G–owned, admin-controlled)
 *
 * Admins can:
 *   - Upload multiple editorial images at once (→ /api/admin/uploads/images)
 *   - Reorder the combined gallery
 *   - Delete editorial images
 *   - Choose ANY image as the primary display image (`primary_image_url`)
 *
 * Editorial images and gallery order/primary selection are NEVER overwritten
 * by future Printful catalog syncs (see server.py sync-catalog handler).
 */

import { useMemo, useRef, useState } from 'react';
import { ArrowUp, ArrowDown, Trash2, Star, Plus, Upload, ImageIcon } from 'lucide-react';
import { api, mediaUrl } from '../../lib/api';

function mergedOrder(order, printful, editorial) {
  const valid = new Set([...(printful || []), ...(editorial || [])]);
  const explicit = (order || []).filter((u) => valid.has(u));
  const explicitSet = new Set(explicit);
  // Append anything valid not yet listed (editorial first, then printful newcomers)
  const rest = [
    ...(editorial || []).filter((u) => !explicitSet.has(u)),
    ...(printful || []).filter((u) => !explicitSet.has(u)),
  ];
  return [...explicit, ...rest];
}

export default function ProductGalleryEditor({
  printfulImages = [],
  editorialImages = [],
  order = [],
  primary = '',
  onChange,           // ({ user_gallery_images, gallery_order, primary_image_url }) => void
  testIdPrefix = 'product-gallery',
}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const combined = useMemo(
    () => mergedOrder(order, printfulImages, editorialImages),
    [order, printfulImages, editorialImages],
  );

  const isEditorial = (url) => editorialImages.includes(url);

  const persist = (nextEditorial, nextOrder, nextPrimary) => {
    onChange?.({
      user_gallery_images: nextEditorial,
      gallery_order: nextOrder,
      primary_image_url: nextPrimary,
    });
  };

  const handleUpload = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setError('');
    setUploading(true);
    try {
      const fd = new FormData();
      Array.from(fileList).forEach((f) => fd.append('files', f));
      const { data } = await api.post('/admin/uploads/images', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const newUrls = (data?.urls || []).filter(Boolean);
      if (newUrls.length === 0) {
        setError('No images uploaded.');
        return;
      }
      const nextEditorial = [...editorialImages, ...newUrls];
      // Insert new editorial images at the front of the explicit order so admins see them first
      const seededOrder = order && order.length > 0 ? order : combined;
      const nextOrder = [...newUrls, ...seededOrder.filter((u) => !newUrls.includes(u))];
      persist(nextEditorial, nextOrder, primary);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Upload failed.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const move = (idx, dir) => {
    const list = [...combined];
    const j = idx + dir;
    if (j < 0 || j >= list.length) return;
    [list[idx], list[j]] = [list[j], list[idx]];
    persist(editorialImages, list, primary);
  };

  const removeEditorial = (url) => {
    if (!isEditorial(url)) return;
    const nextEditorial = editorialImages.filter((u) => u !== url);
    const nextOrder = (order || []).filter((u) => u !== url);
    const nextPrimary = primary === url ? '' : primary;
    persist(nextEditorial, nextOrder, nextPrimary);
  };

  const setPrimary = (url) => {
    persist(editorialImages, order && order.length > 0 ? order : combined, url === primary ? '' : url);
  };

  return (
    <div className="pg-card p-3 -mx-1" data-testid={`${testIdPrefix}-editor`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="pg-label text-[10px]">MERCHANDISING GALLERY</p>
          <p className="text-[11px] text-[var(--pg-ink-muted)] mt-1">
            Combined product gallery. Printful images refresh on sync; your editorial images and order are preserved.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            multiple
            accept="image/*"
            className="hidden"
            onChange={(e) => handleUpload(e.target.files)}
            data-testid={`${testIdPrefix}-upload-input`}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid={`${testIdPrefix}-upload-btn`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-mono tracking-widest uppercase transition-colors border border-[var(--pg-border-2)] hover:border-[var(--pg-gold-2)] text-[var(--pg-ink)] disabled:opacity-60"
          >
            {uploading ? <Upload size={12} className="animate-pulse" /> : <Plus size={12} />}
            {uploading ? 'Uploading…' : 'Add Editorial Images'}
          </button>
        </div>
      </div>

      {error && (
        <div className="text-[11px] font-mono text-[var(--pg-neon-pink)] mb-2" data-testid={`${testIdPrefix}-error`}>
          {error}
        </div>
      )}

      {combined.length === 0 ? (
        <div className="text-center py-6 border border-dashed border-[var(--pg-border-2)] rounded-md text-[var(--pg-ink-muted)] text-xs">
          <ImageIcon size={20} className="mx-auto mb-2 opacity-60" />
          No images yet. Sync a Printful product or upload editorial images above.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid={`${testIdPrefix}-grid`}>
          {combined.map((url, idx) => {
            const editorial = isEditorial(url);
            const isPrimary = primary === url;
            return (
              <div
                key={url + idx}
                data-testid={`${testIdPrefix}-item-${idx}`}
                className="group relative rounded-md overflow-hidden border transition-all"
                style={{
                  borderColor: isPrimary ? 'var(--pg-gold-2)' : 'var(--pg-border-2)',
                  boxShadow: isPrimary ? '0 0 0 2px rgba(226,199,122,0.35)' : 'none',
                }}
              >
                <div className="aspect-square bg-[var(--pg-navy-800)]">
                  <img src={mediaUrl(url)} alt="" className="w-full h-full object-cover" />
                </div>

                {/* Source badge */}
                <span
                  className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded text-[9px] font-mono tracking-widest uppercase"
                  style={{
                    background: editorial ? 'rgba(226,199,122,0.85)' : 'rgba(20,20,32,0.85)',
                    color: editorial ? '#0b0b16' : 'var(--pg-ink)',
                    border: editorial ? '1px solid var(--pg-gold-2)' : '1px solid var(--pg-border-2)',
                  }}
                  data-testid={`${testIdPrefix}-item-${idx}-source`}
                >
                  {editorial ? 'Editorial' : 'Printful'}
                </span>

                {isPrimary && (
                  <span
                    className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded text-[9px] font-mono tracking-widest uppercase"
                    style={{ background: 'var(--pg-gold-2)', color: '#0b0b16' }}
                    data-testid={`${testIdPrefix}-item-${idx}-primary-badge`}
                  >
                    PRIMARY
                  </span>
                )}

                {/* Controls */}
                <div className="absolute inset-x-0 bottom-0 bg-[rgba(11,11,22,0.85)] backdrop-blur-sm px-1.5 py-1 flex items-center justify-between gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => move(idx, -1)}
                      disabled={idx === 0}
                      data-testid={`${testIdPrefix}-item-${idx}-up`}
                      title="Move earlier"
                      className="p-1 rounded hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed text-[var(--pg-ink)]"
                    >
                      <ArrowUp size={12} />
                    </button>
                    <button
                      type="button"
                      onClick={() => move(idx, +1)}
                      disabled={idx === combined.length - 1}
                      data-testid={`${testIdPrefix}-item-${idx}-down`}
                      title="Move later"
                      className="p-1 rounded hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed text-[var(--pg-ink)]"
                    >
                      <ArrowDown size={12} />
                    </button>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setPrimary(url)}
                      data-testid={`${testIdPrefix}-item-${idx}-primary`}
                      title={isPrimary ? 'Currently primary (click to clear)' : 'Set as primary'}
                      className="p-1 rounded hover:bg-white/10"
                      style={{ color: isPrimary ? 'var(--pg-gold-2)' : 'var(--pg-ink)' }}
                    >
                      <Star size={12} fill={isPrimary ? 'var(--pg-gold-2)' : 'none'} />
                    </button>
                    {editorial && (
                      <button
                        type="button"
                        onClick={() => removeEditorial(url)}
                        data-testid={`${testIdPrefix}-item-${idx}-delete`}
                        title="Remove editorial image"
                        className="p-1 rounded hover:bg-white/10 text-[var(--pg-neon-pink)]"
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {combined.length > 0 && (
        <p className="mt-2 text-[10px] font-mono text-[var(--pg-ink-faint)] tracking-wide">
          {editorialImages.length} editorial · {printfulImages.length} from Printful · Primary:{' '}
          {primary ? (
            <span className="text-[var(--pg-gold-2)]">selected</span>
          ) : (
            <span>auto (first in order)</span>
          )}
        </p>
      )}
    </div>
  );
}
